import { useCallback, type ReactNode } from 'react';
import { createElement, Fragment } from 'react';
import {
  useMicController,
  type MicController,
  type MicErrorKind,
  type MicState,
} from './useMicController';
import MicPrePrompt from '@/components/mic/MicPrePrompt';
import MicErrorRecovery from '@/components/mic/MicErrorRecovery';

/**
 * Centralized mic plumbing for any screen that mounts a CodeBaby avatar.
 *
 * Wraps `useMicController` and pre-bundles:
 *   - the pre-prompt + error-recovery overlays (drop `overlays` into JSX)
 *   - the avatar-mount subscription handlers (spread `avatarProps` onto
 *     `<AvatarMount>` to wire mic-data, mic-listening, mic-stopped)
 *   - the mic-data → controller routing (interim vs final, empty-final
 *     guard, optional text callbacks for transcript / input updates)
 *
 * Hosts only need to:
 *   1. Call this hook, passing the toggle target + ready flag.
 *   2. Spread `mic.avatarProps` on their `<AvatarMount>`.
 *   3. Render `<MicButton state={mic.state} onTap={mic.onTap} />`.
 *   4. Render `{mic.overlays}` somewhere at screen level.
 *
 * That's it. No more per-screen subscription wiring or duplicated overlay
 * markup — adding mic to a new screen is one hook call.
 *
 * IMPORTANT: per the loader's behavior (conversationConnection.js:200), the
 * loader auto-calls `this.ask(text)` on mic-final. Do NOT also call ask()
 * from `onFinalText` — that double-sends to the backend. Use `onFinalText`
 * for display-only updates (transcript append, input clear).
 */

export type { MicState, MicErrorKind };

export interface UseAvatarMicOptions {
  /** Calls the avatar session's microphoneToggle. */
  toggle: () => void;
  /** True once the avatar session is ready to accept mic input. */
  sessionReady: boolean;
  /** When set, controller stays idle and ignores taps. */
  disabled?: boolean;
  /**
   * Conversation mode (continuous diarized listener). The mic button becomes a
   * live status indicator, chimes are suppressed, and interim transcripts are
   * NOT streamed into the input — Whisper emits the transcript in a post-speech
   * burst, which reads as a distracting word-by-word "zoom" in the box.
   */
  continuous?: boolean;
  /** Called on each interim transcript update. Use to live-update an input. */
  onInterimText?: (text: string) => void;
  /**
   * Called once with the final transcript text when the user finishes a
   * spoken turn. Use to append to your transcript display. Do NOT call
   * `ask()` here — the loader already did.
   */
  onFinalText?: (text: string) => void;
}

export interface AvatarMic extends MicController {
  /**
   * Props to spread on `<AvatarMount>`. Wires the loader's mic-data,
   * mic-listening, and mic-stopped events into the controller.
   */
  avatarProps: {
    onMicrophoneData: (d: { text: string; isFinal: boolean }) => void;
    onMicrophoneListening: () => void;
    onMicrophoneStopped: () => void;
  };
  /** Pre-rendered modals — drop into screen JSX as `{mic.overlays}`. */
  overlays: ReactNode;
}

export function useAvatarMic(opts: UseAvatarMicOptions): AvatarMic {
  const continuous = opts.continuous ?? false;

  const mic = useMicController({
    toggle: opts.toggle,
    sessionReady: opts.sessionReady,
    continuous,
    ...(opts.disabled !== undefined && { disabled: opts.disabled }),
  });

  const { onInterimText, onFinalText } = opts;

  const onMicrophoneData = useCallback(
    (d: { text: string; isFinal: boolean }) => {
      if (d.isFinal) {
        const trimmed = d.text.trim();
        if (trimmed) onFinalText?.(trimmed);
        mic.onFinal(trimmed.length > 0);
      } else {
        // In conversation mode, skip interim → input: Whisper's post-speech
        // burst would jitter through the box word by word.
        if (!continuous) onInterimText?.(d.text);
        mic.onInterim();
      }
    },
    [mic, continuous, onInterimText, onFinalText],
  );

  const overlays = createElement(
    Fragment,
    null,
    createElement(MicPrePrompt, {
      open: mic.showingPrePrompt,
      onConfirm: mic.confirmPrePrompt,
      onCancel: mic.cancelPrePrompt,
    }),
    createElement(MicErrorRecovery, {
      open: mic.state === 'error',
      errorKind: mic.errorKind,
      onRetry: mic.retry,
      onDismiss: mic.dismissError,
    }),
  );

  return {
    ...mic,
    avatarProps: {
      onMicrophoneData,
      onMicrophoneListening: mic.onSessionMicListening,
      onMicrophoneStopped: mic.onSessionMicIdle,
    },
    overlays,
  };
}
